/**
 * The shared pieces of the custom component definition (limits, identifiers, texts, tones, typed values, formats,
 * action calls): the building blocks of `custom-components.ts` and of the block library (`custom-blocks.ts`).
 * Pure data, browser-safe, no domain words.
 */
import { z } from 'zod';
import { localizedText } from './localized-text';
// -- limits -------------------------------------------------------------------------------------------

export const COMPONENT_LIMITS = {
  props: 24,
  state: 24,
  data: 16,
  actions: 40,
  steps: 20,
  nodes: 400,
  depth: 12,
  columns: 12,
  codeBytes: 200_000,
} as const;

export const COMPONENT_DEFINITION_VERSION = 1 as const;

// -- identifiers --------------------------------------------------------------------------------------

/** The key of a component: the slug of its standalone app (`/apps/<key>`) and its blueprint identity. */
export const ComponentKey = z
  .string()
  .regex(/^[a-z][a-z0-9_-]{0,62}$/, { error: 'Use lower-case letters, digits, "-" and "_", starting with a letter' });
/** Keys of props, state, data sources, actions and action parameters. */
export const SlotKey = z
  .string()
  .regex(/^[a-z][a-z0-9_]{0,39}$/, { error: 'Use lower_snake_case starting with a letter' });
export const NodeId = z.string().regex(/^[A-Za-z0-9_-]{1,40}$/);
export const ApiKey = z.string().regex(/^[a-z][a-z0-9_]{0,62}$/);
export const Text = (max = 300) => localizedText({ maxLength: max });
/** An expression of the one expression language (checked against the context of its place). */
export const ExprText = z.string().max(2000);
/** A text with `{{ expression }}` tokens, per language. */
export const Template = Text(500);

export const Tone = z.enum(['neutral', 'accent', 'success', 'warning', 'danger']);
export type Tone = z.infer<typeof Tone>;
export const Gap = z.enum(['none', 'xs', 'sm', 'md', 'lg']);
export type Gap = z.infer<typeof Gap>;

// -- typed values (props, state, action parameters) ---------------------------------------------------

export const ChoiceOption = z.object({ key: SlotKey, label: Text(80), tone: Tone.optional() }).strict();
export type ChoiceOption = z.infer<typeof ChoiceOption>;

export const ValueType = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('text'), long: z.boolean().optional() }).strict(),
  z.object({ kind: z.literal('number'), min: z.number().optional(), max: z.number().optional() }).strict(),
  z.object({ kind: z.literal('boolean') }).strict(),
  z.object({ kind: z.literal('date') }).strict(),
  z.object({ kind: z.literal('choice'), options: z.array(ChoiceOption).min(1).max(30) }).strict(),
  z.object({ kind: z.literal('record'), object: ApiKey }).strict(),
  z.object({ kind: z.literal('member') }).strict(),
  /** A list of texts (tags, several choices, the ids of uploaded files). */
  z.object({ kind: z.literal('list') }).strict(),
]);
export type ValueType = z.infer<typeof ValueType>;
export const VALUE_KINDS = ['text', 'number', 'boolean', 'date', 'choice', 'record', 'member', 'list'] as const;

// -- formats ------------------------------------------------------------------------------------------

export const ValueFormat = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('auto') }).strict(),
  z
    .object({ kind: z.literal('number'), decimals: z.number().int().min(0).max(4).optional(), unit: Text(20).optional() })
    .strict(),
  z.object({ kind: z.literal('money'), currency: z.string().regex(/^[A-Z]{3}$/).optional() }).strict(),
  z.object({ kind: z.literal('percent'), decimals: z.number().int().min(0).max(2).optional() }).strict(),
  z.object({ kind: z.literal('date'), style: z.enum(['short', 'medium', 'long']).default('medium') }).strict(),
  z.object({ kind: z.literal('text') }).strict(),
]);
export type ValueFormat = z.infer<typeof ValueFormat>;

// -- actions ------------------------------------------------------------------------------------------

/** A call of an action from a node: the arguments are expressions in the context of the node. */
export const ActionRef = z
  .object({ action: SlotKey, args: z.record(SlotKey, ExprText).default({}) })
  .strict();
export type ActionRef = z.infer<typeof ActionRef>;

export const Click = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('action'), call: ActionRef }).strict(),
  z.object({ kind: z.literal('record'), object: ApiKey, record: ExprText }).strict(),
]);
export type Click = z.infer<typeof Click>;
